<template>
  <div class="alert-box-container">
    <!-- Connection status (hidden in production, useful for debugging) -->
    <div v-if="showDebug" class="debug-panel">
      <div :class="['status', isConnected ? 'connected' : 'disconnected']">
        {{ isConnected ? '🟢 Connected' : '🔴 Disconnected' }}
      </div>
      <div v-if="loadingState" class="loading-state">{{ loadingState }}</div>
      <div v-if="errorMessage" class="error">{{ errorMessage }}</div>
    </div>

    <!-- Alert Box Widget -->
    <AlertBoxWidget
      v-if="initialized"
      :settings="settings"
      :user="user"
      :cache="cache"
      :muted="muted"
      @alert-ended="onAlertEnded"
    />

    <!-- Initial loading state -->
    <div v-else-if="!errorMessage" class="loading">
      {{ loadingState || 'Loading...' }}
    </div>

    <!-- Error state -->
    <div v-else class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRoute } from "vue-router";
import { useServerEvents } from "../composables/useServerEvents";
import AlertBoxWidget from "../components/Widgets/AlertBoxWidget/AlertBoxWidget.vue";
import { CUSTOM_EVENTS } from "../api/events";

const API_URL = import.meta.env.VITE_API_URL;

const route = useRoute();
const sceneId = computed(() => route.params.sceneId || "");
const showDebug = computed(() => route.query.debug === "true");
const muted = computed(() => route.query.muted === "true");

const { isConnected, error: sseError, connect, disconnect, addEventListener } = useServerEvents({
  baseUrl: API_URL,
  autoConnect: false,
  refreshToken: () => authenticate(),
});

const initialized = ref(false);
const loadingState = ref("");
const errorMessage = ref("");
const authToken = ref("");
const user = ref(null);
const settings = ref({});
const cache = ref({
  events: [],
});

async function authenticate() {
  loadingState.value = "Authenticating...";
  const response = await fetch(`${API_URL}/scenes/${sceneId.value}/auth`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Authentication failed: ${response.status}`);
  }
  const data = await response.json();
  authToken.value = data.token;
  return data.token;
}

async function fetchAlertBoxConfig() {
  loadingState.value = "Fetching configuration...";
  const response = await fetch(`${API_URL}/scenes/me/config`, {
    headers: {
      "Authorization": `Bearer ${authToken.value}`,
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch config: ${response.status}`);
  }
  const data = await response.json();
  if (data.settings) {
    settings.value = data.settings;
  }
  if (data.userId) {
    user.value = { id: data.userId };
  }
  return data;
}

function handleRequestEvent(data) {
  const { action, request } = data;
  const alertEvent = {
    type: "request",
    subtype: action.toLowerCase(),
    id: request.id,
    data: {
      user_name: request.userName || request.userId,
      request_name: request.product?.name || "Request",
      user_input: request.userInput || "",
      value: request.value || 0,
    },
    request: request,
    mockEvent: request.alertSettings || null,
  };
  const customEvent = new CustomEvent(CUSTOM_EVENTS.REQUEST_ALERT_EVENT, {
    detail: alertEvent,
  });
  window.dispatchEvent(customEvent);
}

function handleConfigUpdate(data) {
  if (data.events) {
    cache.value.events = data.events;
  }
  if (data.settings) {
    settings.value = data.settings;
  }
}

function handleTestAlert(data) {
  const customEvent = new CustomEvent(CUSTOM_EVENTS.REQUEST_ALERT_EVENT, {
    detail: {
      ...data,
      isTest: true,
    },
  });
  window.dispatchEvent(customEvent);
}

function onAlertEnded(alert) {
}

onMounted(async () => {
  if (!sceneId.value) {
    errorMessage.value = "Scene not found.";
    return;
  }

  try {
    await authenticate();
    await fetchAlertBoxConfig();

    loadingState.value = "Connecting to server...";
    connect(authToken.value);

    addEventListener("request:started", handleRequestEvent);
    addEventListener("alert:config", handleConfigUpdate);
    addEventListener("alert:test", handleTestAlert);
    addEventListener("connected", (data) => {
      if (!user.value && data.channelId) {
        user.value = { id: data.channelId };
      }
      loadingState.value = "";
      initialized.value = true;
    });
  } catch (err) {
    errorMessage.value = err.message || "Failed to initialize alert box";
  }
});

onBeforeUnmount(() => {
  disconnect();
});

watch(sseError, (err) => {
  if (err && initialized.value) {
    console.error("[AlertBox] SSE error:", err);
  }
});
</script>

<style scoped>
.alert-box-container {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  position: relative;
  background: transparent;
}

.debug-panel {
  position: fixed;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  z-index: 9999;
}

.status {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status.connected {
  color: #4ade80;
}

.status.disconnected {
  color: #f87171;
}

.loading-state {
  color: #fbbf24;
  margin-top: 4px;
}

.error {
  color: #f87171;
  margin-top: 4px;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.5);
  font-family: sans-serif;
}

.error-message {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #f87171;
  font-family: sans-serif;
  padding: 20px;
  text-align: center;
}
</style>
